// Centralized enemy death handling (spec-arsenal §Death handling): one
// killEnemy() for every killer (weapon projectiles, space-storm firebolts,
// future systems). Releases the enemy, emits the existing `death` event
// (payload shape unchanged — renderer/micro-events depend on it), honors the
// def's drops (xp motes + health-chance roll on the `loot` stream), rolls the
// loot-crate drop (spec-slots: 1.5% trash / 15% elite / 100% boss, same
// stream), and applies the splitter trait. Deterministic: positions, fan
// angles, and rolls derive from sim state and the shared `loot` stream only.

import type { WorldState } from './state.js';
import type { Enemy, Pickup, Vec2 } from '../types.js';
import { wrapArena } from './collision.js';
import { findEnemyDef, initEnemy } from './director.js';

/** Grace telegraph for split-spawn minis — the standard spawn grace (0.2 s). */
const SPLIT_GRACE_TICKS = 12;
/** Heal amount of a dropped health pickup (consumed by updatePickups). */
const HEALTH_PICKUP_VALUE = 20;
/** Radial offset of each mini from the corpse so the fan never stacks. */
const SPLIT_SPAWN_OFFSET = 0.6;
/** Loot-crate drop rates by enemy class (spec-slots): trash, elite, boss. */
const CRATE_CHANCE_NORMAL = 0.015;
const CRATE_CHANCE_ELITE = 0.15;

// Pool-cap fallback: when the pickup pool is full, fold the mote's value
// into the oldest live xp_mote (lowest spawn_tick) instead of dropping the
// XP. Deterministic scan, no RNG. Health pickups and loot crates keep the
// drop-on-full behavior.
export function foldIntoOldestMote(world: WorldState, value: number): void {
  let oldest: Pickup | null = null;
  for (const p of world.pickups.alive()) {
    if (p.pickup_kind !== 'xp_mote') continue;
    if (!oldest || p.spawn_tick < oldest.spawn_tick) oldest = p;
  }
  if (oldest) oldest.value += value;
}

// Same drop a weapon kill always granted (single-mote shape kept from the
// pre-centralization weapon-system/space-storm copies).
function spawnXpMote(world: WorldState, pos: Vec2, tick: number): void {
  const pk = world.pickups.acquire();
  if (!pk) {
    foldIntoOldestMote(world, 1);
    return;
  }

  pk.pos = { x: pos.x, y: pos.y };
  pk.vel = { x: 0, y: 0 };
  pk.pickup_kind = 'xp_mote';
  pk.value = 1;
  pk.magnet = true;
  pk.alive = true;
  pk.spawn_tick = tick;
  pk.targetable = false;

  world.events.emit(
    'spawn',
    { kind: 'pickup', id: pk.id, pickup_kind: 'xp_mote', pos: pk.pos },
    tick
  );
}

function spawnHealthPickup(world: WorldState, pos: Vec2, tick: number): void {
  const pk = world.pickups.acquire();
  if (!pk) return;

  pk.pos = { x: pos.x, y: pos.y };
  pk.vel = { x: 0, y: 0 };
  pk.pickup_kind = 'health';
  pk.value = HEALTH_PICKUP_VALUE;
  pk.magnet = true;
  pk.alive = true;
  pk.spawn_tick = tick;
  pk.targetable = false;

  world.events.emit(
    'spawn',
    { kind: 'pickup', id: pk.id, pickup_kind: 'health', pos: pk.pos, value: pk.value },
    tick
  );
}

// Loot crate (spec-slots): a 'powerup' pickup resolved on collection in
// game.ts updatePickups (weapon → level → motes). Same spawn pattern as the
// health pickup; value 1 is a placeholder, the crate itself is the payload.
function spawnLootCrate(world: WorldState, pos: Vec2, tick: number): void {
  const pk = world.pickups.acquire();
  if (!pk) return;

  pk.pos = { x: pos.x, y: pos.y };
  pk.vel = { x: 0, y: 0 };
  pk.pickup_kind = 'powerup';
  pk.value = 1;
  pk.magnet = true;
  pk.alive = true;
  pk.spawn_tick = tick;
  pk.targetable = false;

  world.events.emit(
    'spawn',
    { kind: 'pickup', id: pk.id, pickup_kind: 'powerup', pos: pk.pos, value: pk.value },
    tick
  );
}

/**
 * Kill enemy `e`: release it, emit the standard `death` event, spawn the
 * def's xp motes, roll `drops.health_chance` on the `loot` stream for a
 * health pickup, roll the loot-crate drop on the same stream (after the
 * health roll), and (splitter trait) fan `split.count` minis out from the
 * corpse with the standard grace telegraph. Minis carry no split field, so
 * they never re-split.
 */
export function killEnemy(world: WorldState, e: Enemy, tick: number): void {
  // Capture identity up front: releasing first lets a split mini reuse this
  // very pool slot, which would overwrite e.def_id/e.pos mid-handler.
  const id = e.id;
  const defId = e.def_id;
  const pos = { x: e.pos.x, y: e.pos.y };
  const elite = e.elite;
  const isBoss = e.boss !== undefined;
  // Killed mid-gestation (spawn telegraph still running): worth a bonus.
  const gestating = e.telegraph_until > tick;
  const def = findEnemyDef(defId);
  // Blood Moon event: double xp motes, health odds, and crate odds while the
  // frenzy burns (write side lives in events/blood-moon.ts).
  const bloodMoon = world.eventFlags?.bloodMoon === true;

  world.enemies.release(e);
  world.events.emit(
    'death',
    { enemy_id: id, kind: 'enemy', def_id: defId, pos: { x: pos.x, y: pos.y }, gestating, boss: isBoss, elite },
    tick
  );

  const moteCount = def.drops.xp_motes * (bloodMoon ? 2 : 1);
  for (let i = 0; i < moteCount; i++) {
    spawnXpMote(world, pos, tick);
  }
  const healthChance = def.drops.health_chance * (bloodMoon ? 2 : 1);
  if (healthChance > 0 && world.rng.get('loot').next() < healthChance) {
    spawnHealthPickup(world, pos, tick);
  }

  // Loot-crate roll (spec-slots): after the xp/health drops, one draw on the
  // 'loot' stream per kill — 1.5% trash, 15% elite, 100% boss. The boss roll
  // is still consumed so every kill draws exactly once from the stream.
  const crateChance = (isBoss ? 1 : elite ? CRATE_CHANCE_ELITE : CRATE_CHANCE_NORMAL) * (bloodMoon ? 2 : 1);
  if (world.rng.get('loot').next() < crateChance) {
    spawnLootCrate(world, pos, tick);
  }

  const split = def.split;
  if (!split) return;
  const miniDef = findEnemyDef(split.def_id);
  const telegraphUntil = tick + SPLIT_GRACE_TICKS;
  for (let i = 0; i < split.count; i++) {
    const mini = world.enemies.acquire();
    if (!mini) {
      world.events.emit('pool_exhausted', { kind: 'enemy' }, tick);
      break;
    }
    // Fan out symmetrically around the corpse; deterministic from the split
    // count alone (no RNG), so replays stay identical.
    const angle = (i / split.count) * Math.PI * 2;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const miniPos = { x: pos.x + dx * SPLIT_SPAWN_OFFSET, y: pos.y + dy * SPLIT_SPAWN_OFFSET };
    wrapArena(miniPos);
    initEnemy(mini, miniDef, miniPos, world.player.pos, world.runTime, tick, telegraphUntil);
    mini.vel = { x: dx * miniDef.max_speed, y: dy * miniDef.max_speed };
    world.events.emit(
      'spawn',
      { kind: 'enemy', def_id: miniDef.id, pos: { x: miniPos.x, y: miniPos.y }, telegraph_until: telegraphUntil },
      tick
    );
  }
}
